import React, { useState, useEffect } from 'react';
import { Shield, AlertCircle, CheckCircle, Lock, Clock } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { verifyVAT, createApplication, getPublicSettings, PublicSettings } from '../../services/certifiedNetworkService';
import {
  isAuthenticated,
  getAssociateData,
  sendOTP,
  verifyOTP,
  saveToken,
  saveAssociateData,
  LocationOption
} from '../../services/otpAuthService';
import { eDipendente } from '../../utils/sezioniDipendente';
import LoadingButton from '../LoadingButton';
import LocationSelectionModal from '../LocationSelectionModal';

interface Step1VerifyVATProps {
  onComplete: (data: { applicationId: string; associate: any }) => void;
}

const Step1VerifyVAT: React.FC<Step1VerifyVATProps> = ({ onComplete }) => {
  const [partitaIVA, setPartitaIVA] = useState('');
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // OTP state
  const [showOTP, setShowOTP] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const [selectedAssociateId, setSelectedAssociateId] = useState<string | null>(null);

  // Multi-sede state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locations, setLocations] = useState<LocationOption[]>([]);

  // Public settings
  const [publicSettings, setPublicSettings] = useState<PublicSettings | null>(null);

  // Carica impostazioni pubbliche
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await getPublicSettings();
        if (response.success) {
          setPublicSettings(response.data);
        }
      } catch (error) {
        console.error('Errore caricamento impostazioni:', error);
      }
    };
    loadSettings();
  }, []);

  // Dipendente dello studio con la sessione aperta: la domanda è del titolare, niente prosecuzione
  // senza OTP con la P.IVA dello studio
  const [riservataAlTitolare, setRiservataAlTitolare] = useState(false);

  // Verifica se già autenticato all'avvio
  useEffect(() => {
    if (isAuthenticated()) {
      const associate = getAssociateData();
      if (associate && eDipendente(associate)) {
        setRiservataAlTitolare(true);
        return;
      }
      if (associate) {
        // Utente già autenticato, usa i suoi dati
        setPartitaIVA(associate.partitaIVA);
        handleVerifyForAuthenticatedUser(associate.partitaIVA);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, []);

  // Gestione countdown OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Verifica P.IVA per utente già autenticato
  const handleVerifyForAuthenticatedUser = async (vat: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await verifyVAT(vat);

      if (response.success) {
        setVerificationResult(response);
        // Procedi direttamente senza OTP
        await handleContinueWithoutOTP(response);
      } else {
        setError(response.message || 'Partita IVA non trovata');
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      const message = errorData?.message || 'Errore durante la verifica';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!partitaIVA.trim()) {
      setError('Inserisci la Partita IVA');
      return;
    }

    // Validazione base P.IVA (11 cifre)
    const cleanedVAT = partitaIVA.replace(/\s/g, '');
    if (!/^\d{11}$/.test(cleanedVAT)) {
      setError('La Partita IVA deve contenere 11 cifre');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await verifyVAT(cleanedVAT);

      if (response.success) {
        setVerificationResult(response);
        toast.success('Partita IVA verificata con successo!');

        // Invia OTP automaticamente
        await handleSendOTP(cleanedVAT);
      } else {
        setError(response.message || 'Partita IVA non trovata');
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      const message = errorData?.message || 'Errore durante la verifica';
      const reason = errorData?.reason;

      // Costruisci messaggio di errore dettagliato
      let fullMessage = message;
      if (reason) {
        fullMessage += `\n\n${reason}`;
      }

      setError(fullMessage);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Invia OTP (con supporto multi-sede)
  const handleSendOTP = async (vat: string, associateId?: string) => {
    try {
      const response = await sendOTP(vat, associateId);

      // Se ci sono più sedi, mostra il modale di selezione
      if (response.multipleLocations && response.locations) {
        setLocations(response.locations);
        setShowLocationModal(true);
        return;
      }

      // Altrimenti procedi normalmente
      if (response.data) {
        setMaskedEmail(response.data.email);
        if (response.data.associateId) {
          setSelectedAssociateId(response.data.associateId);
        }
        setShowOTP(true);
        setCountdown(600); // 10 minuti
        toast.success('Codice OTP inviato via email');
      }
    } catch (err: any) {
      const message = err.message || 'Errore invio OTP';
      toast.error(message);
      setError(message);
    }
  };

  // Gestisce la selezione di una sede dal modale
  const handleLocationSelect = async (locationId: string) => {
    setShowLocationModal(false);
    setSelectedAssociateId(locationId);
    // Invia OTP alla sede selezionata
    await handleSendOTP(partitaIVA, locationId);
  };

  // Verifica OTP
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpCode.trim() || otpCode.length !== 6) {
      setOtpError('Inserisci il codice OTP a 6 cifre');
      return;
    }

    setLoading(true);
    setOtpError(null);

    try {
      const response = await verifyOTP(partitaIVA, otpCode, selectedAssociateId || undefined);

      // Salva token e dati associato
      if (response.data) {
        saveToken(response.data.token);
        saveAssociateData(response.data.associate);

        toast.success('Autenticazione riuscita!');

        // Procedi con la creazione/recupero domanda
        await handleContinueWithoutOTP(verificationResult);
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      const message = errorData?.message || 'Codice OTP non valido';

      setOtpError(message);

      if (errorData?.attemptsRemaining !== undefined) {
        setAttemptsRemaining(errorData.attemptsRemaining);
      }

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Continua senza OTP (utente già autenticato)
  const handleContinueWithoutOTP = async (result: any) => {
    if (!result?.associate) return;

    setLoading(true);
    try {
      if (result.hasExistingApplication) {
        onComplete({
          applicationId: result.existingApplicationId,
          associate: result.associate
        });
      } else {
        const response = await createApplication(
          result.associate.id,
          result.associate.partitaIVA
        );

        if (response.success) {
          onComplete({
            applicationId: response.applicationId,
            associate: result.associate
          });
        }
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Errore durante la creazione della domanda';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-full mb-4">
          {showOTP ? (
            <Lock className="w-8 h-8 text-green-600 dark:text-green-400" />
          ) : (
            <Shield className="w-8 h-8 text-green-600 dark:text-green-400" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {showOTP ? 'Verifica Codice OTP' : 'Verifica Partita IVA'}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {showOTP
            ? `Inserisci il codice OTP inviato a ${maskedEmail}`
            : 'Inserisci la Partita IVA del tuo studio per verificare l\'idoneità all\'iscrizione alla Rete Certificata UNASCA.'
          }
        </p>
      </div>

      {riservataAlTitolare ? (
        <div role="alert" className="max-w-md mx-auto p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
              L'iscrizione alla Rete Certificata è riservata al titolare dello studio.
            </p>
          </div>
        </div>
      ) : showOTP ? (
        // Form OTP
        <form onSubmit={handleVerifyOTP} className="max-w-md mx-auto space-y-4">
          {/* Info email inviata */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-green-800 dark:text-green-300 font-medium mb-1">
                  Codice OTP inviato a {maskedEmail}
                </p>
                <p className="text-xs text-green-700 dark:text-green-400">
                  Controlla la tua casella di posta (anche spam/promozioni)
                </p>
              </div>
            </div>
          </div>

          {/* Avviso email errata */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-1">
                  Email errata o non accessibile?
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  Contatta{' '}
                  <a
                    href={`mailto:${publicSettings?.certifiedNetworkNotificationEmail || 'retecertificata@unasca.it'}`}
                    className="font-semibold underline hover:text-blue-900 dark:hover:text-blue-200"
                  >
                    {publicSettings?.certifiedNetworkNotificationEmail || 'retecertificata@unasca.it'}
                  </a>
                  {' '}per richiedere la sostituzione con una nuova email
                </p>
              </div>
            </div>
          </div>

          <div>
            <label htmlFor="otpCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Codice OTP *
            </label>
            <input
              type="text"
              id="otpCode"
              value={otpCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtpCode(value);
                setOtpError(null);
              }}
              placeholder="123456"
              maxLength={6}
              className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-2xl tracking-widest font-mono focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                otpError ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {otpError && (
              <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800 dark:text-red-400 font-medium">{otpError}</p>
                    {attemptsRemaining < 5 && (
                      <p className="text-xs text-red-700 dark:text-red-500 mt-1">
                        Tentativi rimasti: {attemptsRemaining}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {countdown > 0 && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Clock className="w-4 h-4" />
              <span>Codice valido per: {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}</span>
            </div>
          )}

          <LoadingButton
            type="submit"
            loading={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-medium flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-5 h-5" />
            Verifica e Continua
          </LoadingButton>

          <button
            type="button"
            onClick={() => handleSendOTP(partitaIVA)}
            disabled={countdown > 540 || loading}
            className="w-full text-sm text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {countdown > 540 ? 'Attendi prima di richiedere un nuovo codice' : 'Invia nuovo codice'}
          </button>
        </form>
      ) : !verificationResult ? (
        <form onSubmit={handleVerify} className="max-w-md mx-auto space-y-4">
          <div>
            <label htmlFor="partitaIVA" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Partita IVA *
            </label>
            <input
              type="text"
              id="partitaIVA"
              value={partitaIVA}
              onChange={(e) => {
                setPartitaIVA(e.target.value);
                setError(null);
              }}
              placeholder="12345678901"
              maxLength={11}
              className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                error ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {error && (
              <div className="mt-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800 dark:text-red-300 font-medium mb-2">
                      {error.split('\n\n')[0]}
                    </p>
                    {error.split('\n\n')[1] && (
                      <p className="text-sm text-red-700 dark:text-red-400 mb-3">
                        {error.split('\n\n')[1]}
                      </p>
                    )}
                    <div className="text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 p-3 rounded border border-red-200 dark:border-red-700">
                      <p className="font-medium mb-1">Possibili cause:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        <li>Lo stato dell'associazione non è "ASSOCIATO"</li>
                        <li>Il tesseramento non risulta pagato</li>
                        <li>Sei già iscritto alla Rete Certificata</li>
                        <li>Hai già una domanda in fase di valutazione o approvata</li>
                        <li>La Partita IVA non è presente nel database UNASCA</li>
                      </ul>
                      <p className="mt-3 font-medium">
                        Per assistenza, contatta:{' '}
                        <a
                          href="mailto:retecertificata@unasca.it"
                          className="text-red-900 dark:text-red-300 underline hover:text-red-950 dark:hover:text-red-200"
                        >
                          retecertificata@unasca.it
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <LoadingButton
            type="submit"
            loading={loading}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            Verifica Partita IVA
          </LoadingButton>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Nota:</strong> Solo gli associati UNASCA in regola con la quota associativa
              possono iscriversi alla Rete Certificata.
            </p>
          </div>
        </form>
      ) : null}

      {/* Modale selezione sede (multi-sede) */}
      <LocationSelectionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onSelect={handleLocationSelect}
        locations={locations}
        loading={loading}
      />
    </div>
  );
};

export default Step1VerifyVAT;

